import { CalculatedFieldDefinition, SurveyDefinition } from "../../../types/Survey";
import { getIdentifierValidationError } from "./identifierValidation";
import { FileResultModel, QuestionResultModel, StepResultModel, SurveyResultModel, WebViewStepResultModel } from "../../../types/SurveyResult";
import { evaluateExpression, ExpressionInputs, extractDependencies, ResultType, SurveyAnswerDTO, validateReturnType } from "@careevolution/mydatahelps-cel"
import { getAnswerTypeMappings, SurveyAnswerTypeMapping } from "./answerTypeMappings";
import { convertAnswerToType } from "./answerTypeConversion";
import { formatISO } from "date-fns";
export const calculatedFieldStepIdentifier = "_calculated_fields";

export interface CalculatedFieldSnapshot {
    identifier: string;
    value: string[] | null;
    error: string | null;
}

export interface CalculatedFieldsEvaluation {
    stepResult: StepResultModel;
    fieldSnapshots: CalculatedFieldSnapshot[];
}

export function evaluateCalculatedFields(
    surveyDefinition: SurveyDefinition,
    baseInputs: ExpressionInputs,
    surveyResult: SurveyResultModel): CalculatedFieldsEvaluation | null {
    if (!surveyDefinition.calculatedFields || surveyDefinition.calculatedFields.length === 0) {
        return null;
    }

    const answerTypeMappings = getAnswerTypeMappings(surveyDefinition);
    const now = formatISO(new Date(), { representation: "complete" })
    const finalStepResult: StepResultModel = {
        Identifier: calculatedFieldStepIdentifier,
        StartDate: now,
        EndDate: now,
        QuestionResults: []
    };

    //filter out any existing calculated field results in case this is an edit
    const combinedResult = [...surveyResult.StepResults.filter(f => f.Identifier !== calculatedFieldStepIdentifier), finalStepResult];

    const fieldSnapshots: CalculatedFieldSnapshot[] = [];

    surveyDefinition.calculatedFields.forEach(field => {
        const inputs = {
            ...baseInputs,
        };

        inputs.currentSurveyContext!.currentSurveyAnswers = getCurrentAnswers(combinedResult, answerTypeMappings, surveyResult.Event);
        try {
            const result = evaluateExpression(field.expression.expression, inputs, field.returnType);
            let answers: string[] = [];
            if (field.returnType.startsWith("List")) {
                answers = result.map((r: any) => String(r));
            } else {
                answers = [String(result)];
            }

            // Store the calculated result back into the variables for potential use in subsequent calculations
            finalStepResult.QuestionResults.push({
                Type: "QuestionResult",
                Identifier: field.identifier,
                StartDate: now,
                EndDate: now,
                Answers: answers
            } as QuestionResultModel);

            fieldSnapshots.push({ identifier: field.identifier, value: answers, error: null });
        } catch (error: any) {
            // do nothing on error - values may not yet be present, so errors are expected
            fieldSnapshots.push({ identifier: field.identifier, value: null, error: error?.message ?? "Unknown error" });
        }
    });

    return { stepResult: finalStepResult, fieldSnapshots };
}

export function evaluatePredicateExpression(
    expression: string,
    surveyDefinition: SurveyDefinition,
    baseInputs: ExpressionInputs,
    surveyResult: SurveyResultModel): boolean {

    const answerTypeMappings = getAnswerTypeMappings(surveyDefinition);
    const inputs = {
        ...baseInputs,
    };
    inputs.currentSurveyContext!.currentSurveyAnswers = getCurrentAnswers(surveyResult.StepResults, answerTypeMappings, surveyResult.Event);

    try {
        const result = evaluateExpression(expression, inputs, "Boolean");
        return Boolean(result);
    } catch (error: any) {
        return false;
    }
}

function getCurrentAnswers(stepResults: StepResultModel[], answerTypeMappings: SurveyAnswerTypeMapping[], event?: string): SurveyAnswerDTO[] {
    return stepResults.flatMap(step => {
        return step.QuestionResults.filter(q => q.Type == "QuestionResult" || q.Type == "FileResult" || q.Type == "WebViewStepResult").map(r => {
            let answers: string[] = [];
            if (r.Type === "FileResult") {
                const fr = r as FileResultModel;
                answers = fr.FileName ? [fr.FileName] : [];
            } else if (r.Type === "WebViewStepResult") {
                const wr = r as WebViewStepResultModel;
                answers = wr.Result ? [wr.Result] : [];
            } else {
                const qr = r as QuestionResultModel;
                answers = qr.Answers;
            }

            const answerType = answerTypeMappings.find(a => a.resultIdentifier === r.Identifier && a.stepIdentifier === step.Identifier)?.answerType ?? "Text";

            return {
                stepIdentifier: step.Identifier,
                resultIdentifier: r.Identifier,
                answer: convertAnswerToType(answers, answerType),
                event: event
            };
        });
    });
}

export function validateCalculatedField(calculatedField: CalculatedFieldDefinition, template: SurveyDefinition): string[] {
    const validationErrors: string[] = [];
    if (!calculatedField.identifier || calculatedField.identifier.includes(".")) {
        validationErrors.push("Calculated field must have an identifier and cannot contain dots");
    }
    const identifierError = calculatedField.identifier ? getIdentifierValidationError(calculatedField.identifier, "Calculated field identifier") : null;
    if (identifierError) { validationErrors.push(identifierError); }
    if (template.calculatedFields?.some(field => field !== calculatedField && field.identifier === calculatedField.identifier)) {
        validationErrors.push("Calculated field identifier must be unique");
    }

    const validationError = validateExpression(calculatedField.expression.expression, calculatedField.returnType);
    if (validationError) {
        validationErrors.push(validationError);
    }
    return validationErrors;
}

export function validateExpression(expression: string, returnType: ResultType): string | null {
    if (!expression || expression.trim() === "") {
        return "Expression cannot be empty";
    }
    // validateReturnType formats syntax errors; extractDependencies only adds its own rejections (e.g. non-literal server function arguments).
    const validationError = validateReturnType(expression, returnType);
    if (validationError) {
        return validationError;
    }
    try {
        extractDependencies([expression]);
    } catch (error: any) {
        return `${error.message}`;
    }
    return null;
}